import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Video, ExternalLink, MessageCirclePlus, Lock, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { AskAPersonForm } from "@/components/support/AskAPersonForm";

export const GATHERING_JOIN_URL =
  "https://us02web.zoom.us/w/84562767267?tk=XxRxg6-gYjuAgQHYvzjXaasFDmvKvHivbc2tZhvaZgY.DQkAAAATsFVtoxZoc1V0MXpQTlMzS21LYVdLX0F0TTBRAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";

interface Question {
  id: string;
  question_text: string;
  status: "open" | "pending" | "answered" | "closed";
  route: "gathering" | "private";
  answer_text: string | null;
  answered_at: string | null;
  notified_at: string | null;
  created_at: string;
}

const STATUS_LABEL: Record<Question["status"], string> = {
  open: "Waiting for the congregation",
  pending: "Coming soon",
  answered: "Answered",
  closed: "Not taken up",
};

interface GatheringCardProps {
  openDialogSignal?: number;
}

export function GatheringCard({ openDialogSignal }: GatheringCardProps = {}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (openDialogSignal && openDialogSignal > 0) {
      setOpen(true);
    }
  }, [openDialogSignal]);

  const { data: myQuestions = [] } = useQuery({
    queryKey: ["my-gathering-questions", user?.id],
    enabled: !!user?.id && open,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("list_my_gathering_questions");
      if (error) throw error;
      return ((data ?? []) as Question[]).slice(0, 5);
    },
  });

  return (
    <Card id="this-weeks-gathering" className="border-gold/40 bg-gradient-to-br from-cream/50 to-transparent">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-charcoal">
              <Video className="w-5 h-5 text-gold" />
              This week's congregation
            </CardTitle>
            <CardDescription className="mt-1">
              Come sit with fellow stewards. Thursdays at 1 PM Mountain.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
          The weekly congregation is a space for formation, not a support line. Join live to think alongside
          others, or bring a question ahead of time so it gets attention on the call.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="default" className="bg-gold text-charcoal hover:bg-gold/90">
            <a href={GATHERING_JOIN_URL} target="_blank" rel="noopener noreferrer">
              <Video className="w-4 h-4 mr-2" />
              Join this week
              <ExternalLink className="w-3 h-3 ml-2" />
            </a>
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <MessageCirclePlus className="w-4 h-4 mr-2" />
                Bring a question
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Ask a person</DialogTitle>
                <DialogDescription>
                  Bring it to the weekly congregation, or ask a person privately. You'll get the answer either way.
                </DialogDescription>
              </DialogHeader>

              <AskAPersonForm
                defaultRoute="gathering"
                compact
                onSubmitted={() => {
                  qc.invalidateQueries({ queryKey: ["my-gathering-questions", user?.id] });
                }}
              />

              <div className="border-t pt-3 mt-2">
                <div className="flex items-baseline justify-between mb-2">
                  <h4 className="text-sm font-semibold">Your recent questions</h4>
                  <Link to="/support/questions" className="text-xs text-muted-foreground hover:text-charcoal underline">
                    View all
                  </Link>
                </div>
                {myQuestions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    You haven't asked yet. The first question is often the one that helps everyone.
                  </p>
                ) : (
                  <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {myQuestions.map((q) => (
                      <li key={q.id}>
                        <Link
                          to={`/support/questions/${q.id}`}
                          className="block text-sm border rounded-md p-3 bg-card hover:bg-muted/40 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="flex-1 line-clamp-2">{q.question_text}</p>
                            <div className="flex items-center gap-1 shrink-0">
                              <Badge variant="outline" className="text-[10px]">
                                {q.route === "private" ? (
                                  <><Lock className="w-3 h-3 mr-0.5" />Private</>
                                ) : (
                                  <><Users className="w-3 h-3 mr-0.5" />Congregation</>
                                )}
                              </Badge>
                              <Badge
                                variant={
                                  q.status === "answered" ? "default" : q.status === "closed" ? "outline" : "secondary"
                                }
                              >
                                {STATUS_LABEL[q.status]}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-[10px] text-muted-foreground">
                            Asked {formatDistanceToNow(new Date(q.created_at), { addSuffix: true })}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Button asChild variant="ghost" size="sm" className="text-muted-foreground">
            <Link to="/support/questions">Your questions</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
